import { useState, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Factory, Wrench, Settings, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const workshopVideo = 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/rfgzx81u_WhatsApp%20Video%202026-04-14%20at%208.10.52%20PM%20%283%29.mp4';

const workshopImages = [
  {
    id: 1,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/juy472wy_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2816%29.jpeg',
    title: { en: 'Ready for Installation', ar: 'جاهز للتركيب' },
    description: { en: 'Premium aluminium window frames crafted in our workshop', ar: 'إطارات نوافذ ألمنيوم فاخرة مصنوعة في ورشتنا' },
  },
  {
    id: 2,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/j0v1krto_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2815%29.jpeg',
    title: { en: 'Precision Drilling', ar: 'حفر دقيق' },
    description: { en: 'CNC drilling machine for accurate aluminium profile work', ar: 'آلة حفر CNC للعمل الدقيق على قطاعات الألمنيوم' },
  },
  {
    id: 3,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/clr4p8ig_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2813%29.jpeg',
    title: { en: 'Fabrication Area', ar: 'منطقة التصنيع' },
    description: { en: 'Industrial machinery for professional fabrication', ar: 'معدات صناعية للتصنيع المحترف' },
  },
  {
    id: 4,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/mqk7m9xw_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2812%29.jpeg',
    title: { en: 'Cutting Station', ar: 'محطة القطع' },
    description: { en: 'Makita professional miter saw for precise cuts', ar: 'منشار ماكيتا الاحترافي للقطع الدقيق' },
  },
  {
    id: 5,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/pwqz5mv5_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2814%29.jpeg',
    title: { en: 'Material Storage', ar: 'تخزين المواد' },
    description: { en: 'Organized inventory of aluminium profiles', ar: 'مخزون منظم من قطاعات الألمنيوم' },
  },
  {
    id: 6,
    src: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/b91noize_WhatsApp%20Image%202026-04-14%20at%208.10.52%20PM%20%2811%29.jpeg',
    title: { en: 'Profile Processing', ar: 'معالجة القطاعات' },
    description: { en: 'Advanced machinery for aluminium profile processing', ar: 'آلات متطورة لمعالجة قطاعات الألمنيوم' },
  },
];

const Workshop = () => {
  const { language, isRTL } = useLanguage();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const content = {
    en: {
      badge: 'Behind The Scenes',
      title: 'Our Workshop',
      subtitle: 'Take a look inside our fully-equipped workshop where precision meets craftsmanship',
      videoTitle: 'See Our Craftsmen in Action',
      videoSubtitle: 'Watch how we manufacture premium aluminium products',
      features: [
        { icon: Factory, text: 'State-of-the-art Facility' },
        { icon: Wrench, text: 'Professional Equipment' },
        { icon: Settings, text: 'Precision Manufacturing' },
      ],
    },
    ar: {
      badge: 'خلف الكواليس',
      title: 'ورشتنا',
      subtitle: 'ألقِ نظرة داخل ورشتنا المجهزة بالكامل حيث تلتقي الدقة بالحرفية',
      videoTitle: 'شاهد حرفيينا أثناء العمل',
      videoSubtitle: 'شاهد كيف نصنع منتجات الألمنيوم الفاخرة',
      features: [
        { icon: Factory, text: 'منشأة حديثة' },
        { icon: Wrench, text: 'معدات احترافية' },
        { icon: Settings, text: 'تصنيع دقيق' },
      ],
    },
  };

  const t = content[language];
  const fontFamily = isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif";

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + workshopImages.length) % workshopImages.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % workshopImages.length);
  };

  return (
    <section 
      className="py-20 relative"
      id="workshop"
      data-testid="workshop-section"
    >
      {/* Glassy background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-slate-50/60 to-white/30"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-12 ${isRTL ? 'text-right' : ''}`}>
          <span 
            className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#25D366]/20 to-emerald-400/20 text-[#25D366] text-sm font-medium rounded-full mb-4 backdrop-blur-sm"
            style={{ fontFamily }}
          >
            {t.badge}
          </span>
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-4"
            style={{ fontFamily }}
          >
            {t.title}
          </h2>
          <p 
            className="text-slate-600 max-w-2xl mx-auto text-lg"
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
          >
            {t.subtitle}
          </p>
        </div>

        {/* Features Row */}
        <div className={`flex flex-wrap justify-center gap-6 mb-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {t.features.map((feature, index) => (
            <div 
              key={index}
              className={`flex items-center gap-3 px-5 py-3 glass-card rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <feature.icon size={20} className="text-[#25D366]" />
              <span className="text-slate-700 text-sm font-medium" style={{ fontFamily }}>{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Video Section */}
        <div className="mb-12">
          <div className={`text-center mb-6 ${isRTL ? 'text-right' : ''}`}>
            <h3 
              className="text-xl md:text-2xl font-semibold text-slate-800 mb-2"
              style={{ fontFamily }}
            >
              {t.videoTitle}
            </h3>
            <p 
              className="text-slate-600"
              style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
            >
              {t.videoSubtitle}
            </p>
          </div>
          
          <div className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden glass-card group">
            <video
              ref={videoRef}
              src={workshopVideo}
              className="w-full aspect-video object-cover"
              muted={isMuted}
              loop
              playsInline
              poster={workshopImages[0].src}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              data-testid="workshop-video"
            />
            
            {/* Video Overlay */}
            <div 
              className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity duration-300 ${
                isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'
              }`}
            >
              <button
                onClick={togglePlay}
                className="w-20 h-20 bg-[#25D366] rounded-full flex items-center justify-center hover:bg-[#20BD5A] transition-all hover:scale-110 shadow-2xl"
                data-testid="video-play-btn"
              >
                {isPlaying ? (
                  <Pause size={32} className="text-white" />
                ) : (
                  <Play size={32} className="text-white ml-1" />
                )}
              </button>
            </div>

            {/* Video Controls */}
            <div className={`absolute bottom-4 ${isRTL ? 'left-4' : 'right-4'} flex gap-2`}>
              <button
                onClick={toggleMute}
                className="w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                data-testid="video-mute-btn"
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>

            {/* Gradient Border Effect */}
            <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#25D366]/50 transition-colors pointer-events-none" />
          </div>
        </div>

        {/* Image Grid - Masonry Style */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {workshopImages.map((image, index) => (
            <div
              key={image.id}
              className={`relative group cursor-pointer overflow-hidden rounded-xl ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
              onClick={() => openLightbox(index)}
              data-testid={`workshop-image-${image.id}`}
            >
              <div className={`${index === 0 ? 'aspect-square md:aspect-auto md:h-full' : 'aspect-square'}`}>
                <img
                  src={image.src}
                  alt={image.title[language]}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-sky-900/80 via-sky-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className={`absolute bottom-0 left-0 right-0 p-4 ${isRTL ? 'text-right' : ''}`}>
                  <h3 
                    className="text-white font-semibold text-lg"
                    style={{ fontFamily }}
                  >
                    {image.title[language]}
                  </h3>
                  <p 
                    className="text-sky-200 text-sm mt-1"
                    style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
                  >
                    {image.description[language]}
                  </p>
                </div>
              </div>

              {/* Hover Ring */}
              <div className="absolute inset-0 border-2 border-[#25D366] opacity-0 group-hover:opacity-100 rounded-xl transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="https://wa.me/971566832840?text=Hello%2C%20I%20would%20like%20to%20visit%20your%20workshop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#20BD5A] transition-colors"
            style={{ fontFamily }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {language === 'ar' ? 'احجز زيارة للورشة' : 'Schedule a Workshop Visit'}
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            aria-label="Close lightbox"
          >
            <X size={24} />
          </button>

          {/* Navigation */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors`}
            aria-label="Previous image"
          >
            {isRTL ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-50 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors`}
            aria-label="Next image"
          >
            {isRTL ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[80vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={workshopImages[currentIndex].src}
              alt={workshopImages[currentIndex].title[language]}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className={`mt-4 text-center ${isRTL ? 'text-right' : ''}`}>
              <h3 className="text-white text-xl font-semibold" style={{ fontFamily }}>
                {workshopImages[currentIndex].title[language]}
              </h3>
              <p className="text-slate-400 mt-1" style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}>
                {workshopImages[currentIndex].description[language]}
              </p>
            </div>
          </div>

          {/* Dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {workshopImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentIndex ? 'bg-[#25D366] w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default Workshop;
